"use client"

import React, { useState, useEffect, useMemo } from "react"
import { FormProvider, useForm, useFieldArray, useWatch } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import StickySaveBar from "@/shared/components/sticky-save-bar"
import { Button } from "@/shared/components/ui/button"
import { Plus, X, ChevronDown, Info } from "lucide-react"
import {
    otherLanguageOptions,
    availabilityOptions,
    servingPreferenceOptions,
} from "@/features/auth/constants/select-options"
import { Profile } from "@/features/auth/types/auth-actions.type"
import {
    aboutMeFormSchema,
    baseAboutMeFormSchema,
    AboutMeFormType,
    createAboutMeFormSchema,
    genderOptions,
    tShirtSizeOptions,
} from "../../schemas/profile-form.schema"
import errorToast from "../../../../shared/lib/toast/error.toast"
import successToast from "../../../../shared/lib/toast/success.toast"
import { updateProfile, getProfile, getVolunteerProfile } from "../../actions/profile.action"
import { getDirtyFieldsValues } from "../../../../shared/lib/utils"
import { INVALID_PHONE_ERROR } from "@/features/auth/constants/backend-errors"
import FormTextInput from "@/shared/components/form-inputs/form-text-input"
import FormSelectInput from "../../../../shared/components/form-inputs/form-select-input"
import FormTextareaInput from "../../../../shared/components/form-inputs/form-textarea-input"
import FormOrganizationInput from "@/shared/components/form-inputs/form-organization-input"
import FormNumberRangeInput from "../../../../shared/components/form-inputs/form-number-range-input"
import FormDateRangeInput from "../../../../shared/components/form-inputs/form-date-range-input"
import FormDateInput from "../../../../shared/components/form-inputs/form-date-input"
import FormYesNoInput from "../../../../shared/components/form-inputs/form-yes-no-input"
import FormPhoneInput from "@/shared/components/form-inputs/form-phone-input"
import { StateCountryAutocomplete } from "@/shared/components/form-inputs/StateCountryAutocomplete"
import { Checkbox } from "@/shared/components/ui/checkbox"
import { Label } from "@/shared/components/ui/label"
import { useController } from "react-hook-form"
import DashboardAccordion from "@/shared/components/dashboard-accordion"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/shared/components/ui/accordion"
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/shared/components/ui/form"
import { Input } from "@/shared/components/ui/new-input"
import { Textarea } from "@/shared/components/ui/textarea"
import { cn } from "@/shared/lib/utils"
import { useRegionOptions } from "@/shared/hooks/use-region-options"
import { useSession } from "@/features/auth/context/session.context"
import { useRouter } from "next/navigation"
import { useRegion } from "@/shared/hooks/use-region"
import { buildRegionUrl } from "@/shared/lib/utils"
import { DASHBOARD_ROUTE } from "@/shared/constants/pages-routes"
import { UserRole } from "@/shared/constants/user-roles"
import { BASE_PRETRIP_TAB_ID, SEARCH_PARAMS_TAB_KEY } from "@/features/base/constants/base-tab-links"
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/shared/components/ui/tooltip"
import { getDisplayEmailValue, isNoEmailPlaceholderValue } from "@/features/volunteers/schemas/volunteers.schema"
import {
    sortServePeriodsChronologically,
    sortServePeriodIndicesChronologically,
    findRelevantServePeriodFieldIndexForHighlight,
    hasActiveOrFutureServePeriodForRegion,
} from "@/features/volunteers/lib/serve-periods-display"
import { JAMAICA_VOLUNTEER_REGION_ID } from "@/shared/constants/volunteer-region-ids"
import {
    TravelInsuranceReminderCopy,
    VolunteerTravelSupplement,
} from "@/features/profile/components/forms/volunteer-travel-supplement"
type LocationDetails = {
    city: string
    state: string
    country: string
    latitude?: number
    longitude?: number
    zipCode?: string
}

const numberRangeInputWrapperClassName = "md:col-span-full xl:col-auto"
const scrollingTextareaClassName = "max-h-[72px] overflow-y-auto resize-none"

/** Calculate age in years from date of birth (local date); returns null if invalid or future date. */
function getAgeFromDateOfBirth(date: Date | undefined | null): number | null {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return null
    const today = new Date()
    if (date > today) return null
    let age = today.getFullYear() - date.getFullYear()
    const monthDiff = today.getMonth() - date.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) age -= 1
    return age >= 0 ? age : null
}

function EmailUpdatesCheckbox({ control }: { control: any }) {
    const { field } = useController({
        control,
        name: "emailUpdates",
    })

    return (
        <div className="flex items-center gap-3">
            <Checkbox
                id="emailUpdates"
                checked={field.value ?? true}
                onCheckedChange={(checked) => {
                    field.onChange(checked === true)
                }}
            />
            <Label htmlFor="emailUpdates" className="text-sm cursor-pointer">
                I want to stay updated
            </Label>
        </div>
    )
}

/** Toggle for receiving text messages when someone tags you in a project or group note. */
const TAG_NOTIFICATION_INFO_HEADING = "Tagged note notifications"
const TAG_NOTIFICATION_INFO_TEXT =
    "When someone tags you with @ in a project or group note, you'll get a text notification (requires a phone number above)."

function TagNotificationInfoIcon() {
    const [open, setOpen] = useState(false)

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <TooltipProvider delayDuration={300}>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <PopoverTrigger asChild>
                            <button
                                type="button"
                                className="inline-flex h-5 w-5 items-center justify-center text-muted-foreground hover:text-foreground"
                                aria-label="Tagged note notifications information"
                            >
                                <Info className="h-4 w-4" />
                            </button>
                        </PopoverTrigger>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-[320px] text-xs">
                        <p className="font-medium mb-1">{TAG_NOTIFICATION_INFO_HEADING}</p>
                        <p>{TAG_NOTIFICATION_INFO_TEXT}</p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
            <PopoverContent side="bottom" className="max-w-[340px] text-sm">
                <p className="font-medium mb-1">{TAG_NOTIFICATION_INFO_HEADING}</p>
                <p>{TAG_NOTIFICATION_INFO_TEXT}</p>
            </PopoverContent>
        </Popover>
    )
}

function TagNotificationCheckbox({
    control,
    className,
    checkboxId = "chatNotifications",
}: {
    control: any
    className?: string
    checkboxId?: string
}) {
    const { field } = useController({
        control,
        name: "chatNotifications",
    })

    return (
        <div className={cn("flex items-center gap-3", className)}>
            <Checkbox
                id={checkboxId}
                checked={field.value ?? true}
                onCheckedChange={(checked) => {
                    field.onChange(checked === true)
                }}
            />
            <Label htmlFor={checkboxId} className="text-sm cursor-pointer">
                Text me
            </Label>
            <TagNotificationInfoIcon />
        </div>
    )
}

const PROFILE_ACCORDION_ABOUT_ME = "About Me"
const PROFILE_ACCORDION_SERVE_DATES = "Serve Dates"
const PROFILE_ACCORDION_EXPERIENCE_SKILLS = "Experience & Skills"
const PROFILE_ACCORDION_VEHICLE = "Vehicle"
const PROFILE_ACCORDION_PRAYER_REQUESTS = "Prayer Requests"
const ALL_PROFILE_ACCORDION_ITEMS = [
    PROFILE_ACCORDION_ABOUT_ME,
    PROFILE_ACCORDION_SERVE_DATES,
    PROFILE_ACCORDION_EXPERIENCE_SKILLS,
    PROFILE_ACCORDION_VEHICLE,
    PROFILE_ACCORDION_PRAYER_REQUESTS,
]

function ProfileAboutMeForm({ profile, currentStep, userId }: { profile: Profile; currentStep?: number; userId?: number }) {
    const router = useRouter()
    const { regionSlug } = useRegion()
    const { refreshSession, session } = useSession()
    const [locationDetails, setLocationDetails] =
        useState<LocationDetails | null>(null)
    const [showSuccessBar, setShowSuccessBar] = useState(false)
    const [isFormInitialized, setIsFormInitialized] = useState(false)
    const { options: activeRegionOptions, isLoading: regionsLoading } = useRegionOptions(true)
    // Serve Dates accordion open on load
    const [accordionValue, setAccordionValue] = useState<string[]>([...ALL_PROFILE_ACCORDION_ITEMS])
    const [aboutMeInnerValue, setAboutMeInnerValue] = useState<string[]>(["required", "additional"])
    const [whatElseExpanded, setWhatElseExpanded] = useState(false)
    const [prayerRequestsExpanded, setPrayerRequestsExpanded] = useState(false)
    const [isEmailEditing, setIsEmailEditing] = useState(currentStep === 1)
    const [emailValueBeforeEdit, setEmailValueBeforeEdit] = useState("")
    const availabilityRange =
        profile.availabilityRange &&
        profile.availabilityRange.from &&
        profile.availabilityRange.to
            ? {
                  from: new Date(profile.availabilityRange.from),
                  to: new Date(profile.availabilityRange.to),
              }
            : undefined

    // Helper function to validate enum values
    const isValidEnumValue = <T extends string>(
        value: string | null | undefined,
        validValues: readonly T[]
    ): T | "" | undefined => {
        if (!value) return undefined;
        return validValues.includes(value as T) ? (value as T) : "";
    };

    // Initialize servePeriods from profile.servePeriods (which backend populates from volunteers.serveDates)
    // Otherwise fallback to availabilityRange. Sort chronologically by start date after load.
    const rawServePeriods = profile.servePeriods && Array.isArray(profile.servePeriods) && profile.servePeriods.length > 0
        ? profile.servePeriods.map((period: any) => {
            const parseDate = (dateValue: any): Date | undefined => {
                if (!dateValue) return undefined
                if (dateValue instanceof Date) return dateValue
                if (typeof dateValue === 'string') {
                    if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
                        const [year, month, day] = dateValue.split('-').map(Number)
                        return new Date(year, month - 1, day)
                    }
                    return new Date(dateValue)
                }
                return undefined
            }
            const regionId = period.regionId != null && period.regionId !== ''
                ? (typeof period.regionId === 'number' ? period.regionId : parseInt(period.regionId, 10))
                : undefined
            return {
                from: parseDate(period.from),
                to: parseDate(period.to),
                regionId: regionId != null && !isNaN(regionId) ? String(regionId) : undefined,
            }
        })
        : availabilityRange
          ? [{ from: availabilityRange.from, to: availabilityRange.to, regionId: undefined }]
          : []
    const initialServePeriods = sortServePeriodsChronologically(rawServePeriods)

    // Convert string values from backend to form values
    // Exclude read-only stats fields that are not part of the form
    const {
        hoursWorked,
        daysServed: profileDaysServed,
        trainingVideosWatched,
        workScopesCompleted,
        ...profileWithoutStats
    } = profile
    
    // Convert dateOfBirth from database (string or Date) to Date object for form
    // Handle timezone correctly to avoid date shifting
    let dateOfBirthDate: Date | undefined = undefined
    if (profile.dateOfBirth) {
        // If it's already a Date object, use it directly
        if (profile.dateOfBirth instanceof Date) {
            // Use local date components to avoid timezone shifting
            const date = profile.dateOfBirth
            dateOfBirthDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())
        } else if (typeof profile.dateOfBirth === 'string') {
            // Parse string (YYYY-MM-DD format) using local date components
            const dateStr = profile.dateOfBirth.split('T')[0] // Get just the date part if ISO string
            const [year, month, day] = dateStr.split('-').map(Number)
            if (year && month && day) {
                // Create date in local timezone to avoid shifting
                dateOfBirthDate = new Date(year, month - 1, day)
            }
        }
    }

    let backgroundCheckDate: Date | undefined = undefined
    const bgRaw = (profile as any).backgroundCheck
    if (bgRaw) {
        if (bgRaw instanceof Date) {
            backgroundCheckDate = new Date(bgRaw.getFullYear(), bgRaw.getMonth(), bgRaw.getDate())
        } else if (typeof bgRaw === 'string') {
            const dateStr = bgRaw.split('T')[0]
            const [by, bm, bd] = dateStr.split('-').map(Number)
            if (by && bm && bd) {
                backgroundCheckDate = new Date(by, bm - 1, bd)
            }
        }
    }
    
    const convertedProfile = {
        ...profileWithoutStats,
        availabilityRange,
        servePeriods: initialServePeriods,
        dateOfBirth: dateOfBirthDate,
        backgroundCheck: backgroundCheckDate,
        hasServedBefore: profile.hasServedBefore === "yes" ? "yes" : profile.hasServedBefore === "no" ? "no" : undefined,
        isChristFollower: profile.isChristFollower === "yes" ? "yes" : profile.isChristFollower === "no" ? "no" : undefined,
        emailUpdates: profile.emailUpdates ?? true, // Default to true if not set
        chatNotifications: (profile as any).chatNotifications ?? true, // Default to true; controls SMS when tagged in notes
        organizationName: profile.organizationName ?? "", // Coerce null from backend to empty string
        preferredLocation: profile.preferredLocation ?? "", // Coerce null from backend to empty string
        selectedRegions: (profile as any).selectedRegions?.length
            ? (profile as any).selectedRegions.map((id: number) => String(id))
            : profile.selectedRegion != null
                ? [String(profile.selectedRegion)]
                : [],
        // mealPreparationAbility: 1-5 rating; coerce legacy string values ("prefer_not", etc.) to undefined
        mealPreparationAbility: (() => {
            const mp = profile.mealPreparationAbility
            if (mp === "1" || mp === "2" || mp === "3" || mp === "4" || mp === "5") return parseInt(mp, 10)
            if (typeof mp === "number" && mp >= 1 && mp <= 5) return mp
            return undefined
        })(),
        // Sanitize otherLanguages - convert invalid values to empty string
        otherLanguages: isValidEnumValue(
            profile.otherLanguages,
            otherLanguageOptions.map(opt => opt.value)
        ),
        vehicleMake: (profile as any).vehicleMake ?? "",
        vehicleModel: (profile as any).vehicleModel ?? "",
        vehicleColor: (profile as any).vehicleColor ?? "",
        vehicleLicensePlateNumber: (profile as any).vehicleLicensePlateNumber ?? "",
        vehicleLicensePlateState: (profile as any).vehicleLicensePlateState ?? "",
        gender: (() => {
            const v = isValidEnumValue((profile as any).gender, genderOptions.map((o) => o.value));
            return v === "" ? undefined : v;
        })(),
        tShirtSize: (() => {
            const v = isValidEnumValue((profile as any).tShirtSize, tShirtSizeOptions.map((o) => o.value));
            return v === "" ? undefined : v;
        })(),
        passportNumber: (profile as any).passportNumber ?? undefined,
        emergencyContact1FullName: (profile as any).emergencyContact1FullName ?? undefined,
        emergencyContact1Relationship: (profile as any).emergencyContact1Relationship ?? undefined,
        emergencyContact1Phone: (profile as any).emergencyContact1Phone ?? undefined,
        emergencyContact2FullName: (profile as any).emergencyContact2FullName ?? undefined,
        emergencyContact2Relationship: (profile as any).emergencyContact2Relationship ?? undefined,
        emergencyContact2Phone: (profile as any).emergencyContact2Phone ?? undefined,
        healthInsuranceProvider: (profile as any).healthInsuranceProvider ?? undefined,
        healthInsurancePhone: (profile as any).healthInsurancePhone ?? undefined,
        healthInsurancePolicyNumber: (profile as any).healthInsurancePolicyNumber ?? undefined,
        travelInsuranceProvider: (profile as any).travelInsuranceProvider ?? undefined,
        travelInsurancePhone: (profile as any).travelInsurancePhone ?? undefined,
        travelInsurancePolicyNumber: (profile as any).travelInsurancePolicyNumber ?? undefined,
        jamaicaC5ApplicationComplete: (profile as any).jamaicaC5ApplicationComplete === true,
        jamaicaTripFlightDetails: (profile as any).jamaicaTripFlightDetails ?? undefined,
        healthConditions: (profile as any).healthConditions ?? undefined,
        dietaryRestrictions: (profile as any).dietaryRestrictions ?? undefined,
    }
    
    const parsedProfile = baseAboutMeFormSchema.safeParse(convertedProfile)

    if (!parsedProfile.success) {
        throw new Error(parsedProfile.error.message)
    }

    // selectedRegions for multi-select (array of strings for form)
    const selectedRegionsForForm = (parsedProfile.data as any).selectedRegions?.length
        ? (parsedProfile.data as any).selectedRegions.map((id: number) => String(id))
        : parsedProfile.data.selectedRegion != null
            ? [String(parsedProfile.data.selectedRegion)]
            : []

    // Create dynamic schema based on currentStep
    const dynamicSchema = React.useMemo(() => {
        return createAboutMeFormSchema(currentStep)
    }, [currentStep])
    
    const form = useForm<AboutMeFormType>({
        resolver: zodResolver(dynamicSchema),
        defaultValues: {
            ...parsedProfile.data,
            phone: parsedProfile.data.phone ?? '',
            selectedRegions: selectedRegionsForForm,
            servePeriods: (initialServePeriods.length > 0 ? initialServePeriods : [{ from: undefined, to: undefined, regionId: undefined }]) as AboutMeFormType['servePeriods'],
            emailUpdates: parsedProfile.data.emailUpdates ?? true, // Default to true
            chatNotifications: parsedProfile.data.chatNotifications ?? true, // Default to true
        },
    })

    const {
        getValues,
        formState,
        reset,
        control,
        watch,
        setValue,
        trigger,
    } = form
    
    const { dirtyFields, isDirty, isSubmitting } = formState

    const { fields, append, remove } = useFieldArray({
        control,
        name: "servePeriods",
    })

    const watchedServePeriods = useWatch({ control, name: "servePeriods" })
    const sortedServeFieldIndices = React.useMemo(
        () =>
            sortServePeriodIndicesChronologically(fields.length, (i) => ({
                from: watchedServePeriods?.[i]?.from,
                to: watchedServePeriods?.[i]?.to,
            })),
        [fields.length, watchedServePeriods]
    )
    const relevantServeFieldIndex = React.useMemo(
        () =>
            findRelevantServePeriodFieldIndexForHighlight(sortedServeFieldIndices, (i) => ({
                from: watchedServePeriods?.[i]?.from,
                to: watchedServePeriods?.[i]?.to,
            })),
        [sortedServeFieldIndices, watchedServePeriods]
    )

    const watchedSelectedRegions = useWatch({ control, name: "selectedRegions" })
    const showJamaicaVolunteerTravel = useMemo(() => {
        if (
            hasActiveOrFutureServePeriodForRegion(
                watchedServePeriods as Array<{ from?: unknown; to?: unknown; regionId?: unknown }>,
                JAMAICA_VOLUNTEER_REGION_ID
            )
        ) {
            return true
        }
        const wr = watchedSelectedRegions
        if (Array.isArray(wr) && wr.length > 0) {
            const n = parseInt(String(wr[0]), 10)
            if (!isNaN(n)) return n === JAMAICA_VOLUNTEER_REGION_ID
        }
        const rid = (profile as any).regionId ?? profile.selectedRegion
        return rid === JAMAICA_VOLUNTEER_REGION_ID
    }, [watchedSelectedRegions, watchedServePeriods, profile])

    const skillsAndQualitiesField = useController({ control, name: "skillsAndQualities" })
    const prayerRequestField = useController({ control, name: "prayerRequest" })

    // Ensure Serve Dates accordion is open on load so user sees serve periods
    useEffect(() => {
        setAccordionValue((prev) =>
            prev.includes(PROFILE_ACCORDION_SERVE_DATES) ? prev : [...prev, PROFILE_ACCORDION_SERVE_DATES]
        )
    }, [])

    // Track if this is the initial mount to prevent marking form dirty on load
    const isInitialMount = React.useRef(true)

    // Store the initial form values for reset
    const initialFormValues = React.useRef<AboutMeFormType | null>(null)
    
    // Reset form after initialization to ensure it starts clean
    useEffect(() => {
        // Use a delay to ensure all initialization is complete
        const timer = setTimeout(() => {
            // Get current form values (which react-hook-form is already using internally)
            // These are the actual Date object instances that react-hook-form sees
            const currentFormValues = form.getValues()
            
            // Deep clone the values to create new instances but preserve structure
            // This ensures we're using the exact same structure that react-hook-form has
            const valuesToUseAsDefaults = JSON.parse(JSON.stringify(currentFormValues))
            
            // Ensure preferredLocation is preserved (JSON.parse/stringify should handle it, but be explicit)
            if (convertedProfile.preferredLocation !== undefined && convertedProfile.preferredLocation !== null) {
                valuesToUseAsDefaults.preferredLocation = convertedProfile.preferredLocation
            }
            
            // Ensure selectedRegions is array of strings for multi-select
            if (Array.isArray((convertedProfile as any).selectedRegions) && (convertedProfile as any).selectedRegions.length > 0) {
                valuesToUseAsDefaults.selectedRegions = (convertedProfile as any).selectedRegions.map((id: number) => String(id))
            } else if (convertedProfile.selectedRegion !== undefined && convertedProfile.selectedRegion !== null) {
                valuesToUseAsDefaults.selectedRegions = [String(convertedProfile.selectedRegion)]
            }
            
            // Convert date strings back to Date objects
            if (valuesToUseAsDefaults.dateOfBirth) {
                valuesToUseAsDefaults.dateOfBirth = new Date(valuesToUseAsDefaults.dateOfBirth)
            }
            if (valuesToUseAsDefaults.availabilityRange) {
                if (valuesToUseAsDefaults.availabilityRange.from) {
                    valuesToUseAsDefaults.availabilityRange.from = new Date(valuesToUseAsDefaults.availabilityRange.from)
                }
                if (valuesToUseAsDefaults.availabilityRange.to) {
                    valuesToUseAsDefaults.availabilityRange.to = new Date(valuesToUseAsDefaults.availabilityRange.to)
                }
            }
            if (Array.isArray(valuesToUseAsDefaults.servePeriods)) {
                valuesToUseAsDefaults.servePeriods = valuesToUseAsDefaults.servePeriods.map((period: any) => ({
                    ...period,
                    from: period.from ? new Date(period.from) : undefined,
                    to: period.to ? new Date(period.to) : undefined,
                    regionId: period.regionId,
                }))
            }
            
            // Store for later use
            initialFormValues.current = valuesToUseAsDefaults
            
            // Reset with these values and update defaults
            // This ensures defaults match exactly what's in the form
            reset(valuesToUseAsDefaults, { 
                keepDefaultValues: false, // Update defaults to match current values
                keepDirty: false,
                keepErrors: false,
                keepIsSubmitted: false,
                keepTouched: false,
                keepIsValid: false,
                keepSubmitCount: false,
            })
            
            isInitialMount.current = false
            
            // Wait for the form state to update
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    // Check if form is now clean
                    const currentIsDirty = form.formState.isDirty
                    
                    if (!currentIsDirty) {
                        setIsFormInitialized(true)
                    } else {
                        // If still dirty after reset, it might be a timing issue
                        // Try one more reset with the exact current values
                        const latestValues = form.getValues()
                        reset(latestValues, { 
                            keepDefaultValues: false,
                            keepDirty: false,
                        })
                        
                        // Mark as initialized after a short delay
                        // At this point, we've done everything we can
                        setTimeout(() => {
                            setIsFormInitialized(true)
                        }, 100)
                    }
                })
            })
        }, 250) // Increased delay to ensure all form initialization is complete
        
        return () => clearTimeout(timer)
    }, [reset, form]) // eslint-disable-line react-hooks/exhaustive-deps

    // New users (currentStep === 1): ensure sticky save bar can show when they edit — set initialized after a short delay so the bar appears when form is dirty
    useEffect(() => {
        if (currentStep !== 1) return
        const fallback = setTimeout(() => setIsFormInitialized((prev) => prev || true), 500)
        return () => clearTimeout(fallback)
    }, [currentStep])

    // Ensure at least one period exists on mount (without marking form dirty)
    useEffect(() => {
        if (fields.length === 0 && isFormInitialized) {
            append({ from: undefined, to: undefined, regionId: undefined }, { shouldFocus: false })
        }
    }, [fields.length, isFormInitialized]) // eslint-disable-line react-hooks/exhaustive-deps

    const experienceConstruction = watch("experienceConstruction")
    const experienceManualLabor = watch("experienceManualLabor")
    const experienceLeadingTeams = watch("experienceLeadingTeams")
    const experienceDataManagement = watch("experienceDataManagement")

    const onSubmit = async () => {
        // Validate form before submission, especially if currentStep === 1
        const isValid = await trigger()
        if (!isValid) {
            errorToast('Please complete all required fields before saving.')
            // Find and scroll to first error (including nested paths like servePeriods.0.from)
            const findFirstError = (errors: Record<string, unknown>, path = ''): string | null => {
                for (const key in errors) {
                    const currentPath = path ? `${path}.${key}` : key
                    const err = errors[key] as { message?: string } | undefined
                    if (err?.message) return currentPath
                    if (typeof err === 'object' && err !== null && !('message' in err)) {
                        const nested = findFirstError(err as Record<string, unknown>, currentPath)
                        if (nested) return nested
                    }
                }
                return null
            }
            const firstErrorPath = findFirstError(form.formState.errors as Record<string, unknown>)
            if (firstErrorPath) {
                setTimeout(() => {
                    form.setFocus(firstErrorPath as any, { shouldSelect: false })
                }, 100)
            }
            return
        }
        
        const values = getValues()
        
        const dirtyFieldsValues = getDirtyFieldsValues(dirtyFields, values)
        
        // Required fields: Full Name, Email, Phone, Address, Organization
        const { transformToE164 } = await import('@/shared/lib/utils/phone.utils')
        
        const normalizedEmail =
            typeof values.email === "string" ? values.email.trim().toLowerCase() : ""
        const shouldSendEmail =
            currentStep === 1 || (!!dirtyFields.email && normalizedEmail.length > 0)

        const requiredFields = {
            fullname: values.fullname,
            phone: values.phone && typeof values.phone === 'string'
                ? transformToE164(values.phone)
                : values.phone,
            preferredLocation: values.preferredLocation,
            organizationName: values.organizationName,
        }
        
        // Calculate daysServed from all serve periods
        // Only counts days up to the current date (excludes future dates)
        let totalDays = 0
        const today = new Date()
        const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate())
        
        if (values.servePeriods && Array.isArray(values.servePeriods)) {
            values.servePeriods.forEach((period: any) => {
                if (period?.from && period?.to) {
                    const fromDate = new Date(period.from)
                    const toDate = new Date(period.to)
                    
                    // Normalize to midnight to avoid timezone issues
                    const start = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate())
                    const end = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate())
                    
                    // Only count if the period has started (start date is in the past or today)
                    if (start <= todayMidnight) {
                        // Cap the end date to today if it's in the future
                        const effectiveEnd = end > todayMidnight ? todayMidnight : end
                        
                        // Calculate the difference in days (inclusive of both dates)
                        const timeDiff = effectiveEnd.getTime() - start.getTime()
                        const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24)) + 1 // +1 to include both start and end dates
                        
                        if (daysDiff > 0) {
                            totalDays += daysDiff
                        }
                    }
                }
            })
        } else if (dirtyFieldsValues.availabilityRange && values.availabilityRange?.from && values.availabilityRange?.to) {
            // Fallback to single availabilityRange for backward compatibility
            const fromDate = new Date(values.availabilityRange.from)
            const toDate = new Date(values.availabilityRange.to)
            
            // Normalize to midnight to avoid timezone issues
            const start = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate())
            const end = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate())
            
            // Only count if the period has started (start date is in the past or today)
            if (start <= todayMidnight) {
                // Cap the end date to today if it's in the future
                const effectiveEnd = end > todayMidnight ? todayMidnight : end
                
                const timeDiff = effectiveEnd.getTime() - start.getTime()
                const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24)) + 1
                
                if (daysDiff > 0) {
                    totalDays = daysDiff
                }
            }
        }
        
        // Create a properly typed object that includes calculated fields
        // Always include required fields, then merge with dirty fields (dirty fields take precedence)
        const submissionData: Record<string, any> = {
            ...requiredFields,
            ...dirtyFieldsValues,
            // Ensure required fields are not overridden by empty values from dirty fields
            fullname: dirtyFieldsValues.fullname ?? requiredFields.fullname,
            phone: dirtyFieldsValues.phone && typeof dirtyFieldsValues.phone === 'string'
                ? transformToE164(dirtyFieldsValues.phone)
                : requiredFields.phone,
            preferredLocation: dirtyFieldsValues.preferredLocation ?? requiredFields.preferredLocation,
            organizationName: dirtyFieldsValues.organizationName ?? requiredFields.organizationName,
        }
        if (shouldSendEmail) {
            submissionData.email = normalizedEmail
        }

        // Normalize optional phone fields to E.164 when they are part of dirty payload updates.
        const optionalPhoneFields = [
            "emergencyContact1Phone",
            "emergencyContact2Phone",
            "healthInsurancePhone",
            "travelInsurancePhone",
        ] as const
        optionalPhoneFields.forEach((field) => {
            const fieldValue = dirtyFieldsValues[field]
            if (fieldValue === undefined) return
            if (typeof fieldValue === "string") {
                const trimmed = fieldValue.trim()
                submissionData[field] = trimmed ? transformToE164(trimmed) : fieldValue
                return
            }
            submissionData[field] = fieldValue
        })
        
        // Update daysServed if we have valid periods
        if (totalDays > 0) {
            submissionData.daysServed = totalDays
        }
        
        // Include selectedRegions (multi-select) if provided
        const selectedRegionsValue = dirtyFieldsValues.selectedRegions ?? values.selectedRegions
        if (Array.isArray(selectedRegionsValue) && selectedRegionsValue.length > 0) {
            submissionData.selectedRegions = selectedRegionsValue.map((v) =>
                typeof v === 'string' ? parseInt(v, 10) : v
            ).filter((n) => !isNaN(n) && n > 0)
        }
        
        // Remove age from submissionData - we only save dateOfBirth
        // This ensures age is never sent to the backend
        if (submissionData.hasOwnProperty('age')) {
            delete submissionData.age
        }
        
        // Ensure dateOfBirth is properly formatted as YYYY-MM-DD string if it exists
        // The Date object is created at noon UTC from local date components in FormDateInput
        // We need to extract the date components that were used to create it (the local components)
        // Since the date represents a date-only value, we extract using the local date methods
        // to get the date as it appears to the user, which matches what they selected
        if (submissionData.dateOfBirth && submissionData.dateOfBirth instanceof Date) {
            const date = submissionData.dateOfBirth

            // Extract using local methods to get the date as the user sees it
            // This works because the date was created from local components at noon UTC
            // When displayed in the user's timezone, it will show the correct day
            const year = date.getFullYear()
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const day = String(date.getDate()).padStart(2, '0')
            const dateString = `${year}-${month}-${day}`

            submissionData.dateOfBirth = dateString
        }

        if (submissionData.backgroundCheck && submissionData.backgroundCheck instanceof Date) {
            const d = submissionData.backgroundCheck
            const y = d.getFullYear()
            const m = String(d.getMonth() + 1).padStart(2, '0')
            const day = String(d.getDate()).padStart(2, '0')
            submissionData.backgroundCheck = `${y}-${m}-${day}`
        }
        if (dirtyFields.backgroundCheck && values.backgroundCheck === undefined) {
            submissionData.backgroundCheck = null
        }
        
        // Process servePeriods: Convert to ISO strings and calculate availabilityRange
        // CRITICAL: Always overwrite submissionData.servePeriods when it's in the form.
        // If we have blank periods (from adding a row and not filling dates), dirtyFieldsValues
        // can contain servePeriods with undefined from/to. We must never send those to the schema.
        if (values.servePeriods && Array.isArray(values.servePeriods) && values.servePeriods.length > 0) {
            const validPeriods = values.servePeriods.filter((p: any) => p?.from && p?.to)
            if (validPeriods.length > 0) {
                // Convert servePeriods Date objects to ISO 8601 strings for backend
                submissionData.servePeriods = validPeriods.map((p: any) => {
                    const fromDate = p.from instanceof Date ? p.from : new Date(p.from)
                    const toDate = p.to instanceof Date ? p.to : new Date(p.to)
                    const entry: { from: string; to: string; regionId?: number } = {
                        from: fromDate.toISOString(),
                        to: toDate.toISOString(),
                    }
                    if (p.regionId != null && p.regionId !== '' && p.regionId !== undefined) {
                        const rid = typeof p.regionId === 'number' ? p.regionId : parseInt(String(p.regionId), 10)
                        if (!isNaN(rid)) entry.regionId = rid
                    }
                    return entry
                })
                
                // Calculate availabilityRange from all periods
                const dates = validPeriods.map((p: any) => ({
                    from: new Date(p.from),
                    to: new Date(p.to),
                }))
                const earliestStart = new Date(Math.min(...dates.map(d => d.from.getTime())))
                const latestEnd = new Date(Math.max(...dates.map(d => d.to.getTime())))
                
                submissionData.availabilityRange = {
                    from: earliestStart,
                    to: latestEnd,
                }
            } else {
                // All periods are blank - send empty array so we don't pass invalid { from: undefined, to: undefined } to schema
                submissionData.servePeriods = []
            }
        } else if (dirtyFieldsValues.servePeriods !== undefined) {
            // servePeriods was in dirty fields but values has none - ensure we don't pass invalid data
            submissionData.servePeriods = []
        }
        
        // Ensure preferredLocation is included if it exists in form values
        // This handles the case where StateCountryAutocomplete sets it but it might not be marked as dirty
        if (values.preferredLocation !== undefined && values.preferredLocation !== null && values.preferredLocation !== '') {
            submissionData.preferredLocation = values.preferredLocation
        }
        
        // Ensure prayerRequest is included if it exists in form values
        // This ensures prayer requests are saved even if the field wasn't marked as dirty
        if (values.prayerRequest !== undefined && values.prayerRequest !== null) {
            submissionData.prayerRequest = values.prayerRequest.trim() || null
        }

        // Ensure chatNotifications (and emailUpdates) are included when in form values
        if (values.chatNotifications !== undefined) {
            submissionData.chatNotifications = values.chatNotifications
        }
        if (values.emailUpdates !== undefined) {
            submissionData.emailUpdates = values.emailUpdates
        }

        // Ensure all experience/rating fields (1-5) and mealPreparationAbility are included from form values
        // So Leading at work site, Data management, Cooking meals, and all other skills save correctly
        const experienceRatingFields = [
            'experienceConstruction', 'experienceManualLabor', 'experienceDataManagement', 'experienceStorytelling', 'mealPreparationAbility',
            'experienceLeadingTeams', 'experienceDrywallFinishing', 'experienceRoughCarpentry', 'experienceFinishCarpentry',
            'experienceFlooring', 'experienceWoodWorking', 'experienceElectrical', 'experiencePlumbing',
            'experienceHvac', 'experienceArborist', 'experienceRoofing', 'experienceHeavyEquipment',
            'experienceAltMinistries',
        ] as const
        experienceRatingFields.forEach((field) => {
            const v = values[field]
            if (v !== undefined && v !== null && (typeof v !== 'string' || v !== '')) {
                const num = typeof v === 'number' ? v : parseInt(String(v), 10)
                if (!isNaN(num) && num >= 1 && num <= 5) submissionData[field] = num
            }
        })

        // Use my-profile API for self-service profile updates. The PATCH /user/:id endpoint
        // is restricted to Admin/BaseLeader only, so volunteers would get "Forbidden resource".
        const res = await updateProfile(submissionData)

        if (!res.success) {
            if (res.message === INVALID_PHONE_ERROR) {
                form.setError(
                    "phone",
                    { message: "Invalid phone number" },
                    { shouldFocus: true }
                )
                return
            }
            errorToast(res.message)
            return
        }
        successToast("Profile updated successfully")
        
        // Refetch profile so servePeriods (including regionId) come from server and region dropdown doesn't show blank
        const refetched =
            typeof userId === "number"
                ? await getVolunteerProfile(userId)
                : await getProfile()
        const currentValues = getValues()
        if (refetched.success && refetched.data) {
            const latestProfile = refetched.data as any
            // Sync phone fields from server so users immediately see normalized/saved formatting.
            const phoneFields = [
                "phone",
                "emergencyContact1Phone",
                "emergencyContact2Phone",
                "healthInsurancePhone",
                "travelInsurancePhone",
            ] as const
            phoneFields.forEach((fieldName) => {
                if (latestProfile[fieldName] !== undefined) {
                    ;(currentValues as any)[fieldName] =
                        latestProfile[fieldName] ?? ""
                }
            })
        }
        if (refetched.success && refetched.data?.servePeriods && Array.isArray(refetched.data.servePeriods) && refetched.data.servePeriods.length > 0) {
          const servePeriodsFromServer = refetched.data.servePeriods.map((period: any) => {
            const parseDate = (dateValue: any): Date | undefined => {
              if (!dateValue) return undefined
              if (dateValue instanceof Date) return dateValue
              if (typeof dateValue === 'string') {
                if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
                  const [y, m, d] = dateValue.split('-').map(Number)
                  return new Date(y, m - 1, d)
                }
                return new Date(dateValue)
              }
              return undefined
            }
            const rawRegionId = period.regionId != null && period.regionId !== ''
              ? period.regionId
              : undefined
            const regionId = rawRegionId != null
              ? (typeof rawRegionId === 'number' ? rawRegionId : parseInt(String(rawRegionId), 10))
              : undefined
            return {
              from: parseDate(period.from),
              to: parseDate(period.to),
              regionId: regionId != null && !isNaN(regionId) ? regionId : undefined,
            }
          })
          currentValues.servePeriods = sortServePeriodsChronologically(servePeriodsFromServer)
        }
        
        // Mark form as clean by resetting to current values (which are now saved)
        reset(currentValues, {
            keepDefaultValues: false, // Update defaults to current values
            keepDirty: false,
            keepErrors: false,
            keepIsSubmitted: false,
            keepTouched: false,
            keepIsValid: false,
            keepSubmitCount: false,
        })
        
        // Update stored initial values
        if (initialFormValues.current) {
            initialFormValues.current = currentValues
        }
        
        // Show visual feedback that form was saved
        setShowSuccessBar(true)

        // Keep shared SessionContext profile in sync after successful save.
        await refreshSession()

        // First-time completion (required fields): send volunteers to Hub PreTrip once; other saves stay on Profile.
        const wasIncomplete = currentStep === 1
        const newStep = refetched.success && refetched.data ? refetched.data.currentStep : undefined
        if (
            wasIncomplete &&
            typeof newStep === "number" &&
            newStep !== 1 &&
            session?.role === UserRole.VOLUNTEER
        ) {
            const hubPreTripHref = buildRegionUrl(regionSlug ?? null, DASHBOARD_ROUTE.base, {
                [SEARCH_PARAMS_TAB_KEY]: BASE_PRETRIP_TAB_ID,
            })
            router.replace(hubPreTripHref)
        }
    }
    
    // Hide success bar when form becomes dirty again
    useEffect(() => {
        if (isDirty && showSuccessBar) {
            setShowSuccessBar(false)
        }
    }, [isDirty, showSuccessBar])
    
    // Handler for sticky bar save button
    const handleStickySave = async () => {
        // Validate form before submission, especially if currentStep === 1
        const isValid = await trigger()
        if (!isValid) {
            errorToast('Please complete all required fields before saving.')
            // Find and scroll to first error (including nested paths like servePeriods.0.from)
            const findFirstError = (errors: Record<string, unknown>, path = ''): string | null => {
                for (const key in errors) {
                    const currentPath = path ? `${path}.${key}` : key
                    const err = errors[key] as { message?: string } | undefined
                    if (err?.message) return currentPath
                    if (typeof err === 'object' && err !== null && !('message' in err)) {
                        const nested = findFirstError(err as Record<string, unknown>, currentPath)
                        if (nested) return nested
                    }
                }
                return null
            }
            const firstErrorPath = findFirstError(form.formState.errors as Record<string, unknown>)
            if (firstErrorPath) {
                setTimeout(() => {
                    form.setFocus(firstErrorPath as any, { shouldSelect: false })
                }, 100)
            }
            return
        }
        // Just get the current form data and submit it
        // The onSubmit function already handles validation and dirty field checking
        await onSubmit()
    }

    const experienceSkillsSummary =
        accordionValue.includes(PROFILE_ACCORDION_EXPERIENCE_SKILLS)
            ? null
            : [
                  `General construction: ${experienceConstruction ?? "—"}`,
                  `Manual labor: ${experienceManualLabor ?? "—"}`,
                  `Leading at work site: ${experienceLeadingTeams ?? "—"}`,
                  `Data management: ${experienceDataManagement ?? "—"}`,
              ].join(" · ")

    const allSectionsOpen = accordionValue.length === ALL_PROFILE_ACCORDION_ITEMS.length
    const handleExpandCollapseAll = () => {
        setAccordionValue(allSectionsOpen ? [] : [...ALL_PROFILE_ACCORDION_ITEMS])
    }

    return (
        <FormProvider {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit)}
                noValidate
                className="grid gap-5 md:gap-3 pb-12"
            >
                <div className="flex justify-end">
                    <Button
                        type="button"
                        variant="default"
                        size="sm"
                        onClick={handleExpandCollapseAll}
                        className="bg-primary hover:bg-primary/90 text-white font-bold"
                    >
                        {allSectionsOpen ? "Collapse All" : "Expand All"}
                    </Button>
                </div>
                <Accordion
                    type="multiple"
                    value={accordionValue}
                    onValueChange={setAccordionValue}
                    className="space-y-5"
                >
                    <DashboardAccordion heading={PROFILE_ACCORDION_ABOUT_ME} className="bg-white">
                        <Accordion
                            type="multiple"
                            value={aboutMeInnerValue}
                            onValueChange={setAboutMeInnerValue}
                            className="space-y-3 w-full"
                        >
                            <AccordionItem value="required" className="border rounded-lg bg-muted p-4 px-4">
                                <AccordionTrigger className="pt-3 pb-0 text-base font-semibold">
                                    Required
                                </AccordionTrigger>
                                <AccordionContent className="pt-0">
                                    <div className="space-y-5 md:space-y-4">
                                        {/* Row 1: Full Name, Phone, Notifications, Email */}
                                        <div className="grid grid-cols-1 gap-5 items-end md:grid-cols-3 md:gap-3">
                                            <FormTextInput
                                                inputGroupProps={{ label: "Full Name*", highlightWhenEmpty: true }}
                                                placeholder="Full Name*"
                                                control={control}
                                                name="fullname"
                                            />
                                            <FormPhoneInput
                                                inputGroupProps={{
                                                    label: (
                                                        <div className="flex items-center gap-3 flex-wrap">
                                                            <span>Phone Number*</span>
                                                            <TagNotificationCheckbox
                                                                control={control}
                                                                checkboxId="chatNotificationsInline"
                                                            />
                                                        </div>
                                                    ),
                                                    highlightWhenEmpty: true,
                                                }}
                                                placeholder="Phone Number*"
                                                name="phone"
                                                control={control}
                                            />
                                            <FormField
                                                control={control}
                                                name="email"
                                                render={({ field, fieldState }) => {
                                                    const emailValue = String(field.value ?? "")
                                                    const isRequiredEmailMissing =
                                                        emailValue.trim() === "" || isNoEmailPlaceholderValue(emailValue)
                                                    const requiredEmailHighlightStyle = isRequiredEmailMissing
                                                        ? { backgroundColor: "var(--color-pizza-600)", opacity: 0.3, color: "#000000" }
                                                        : undefined

                                                    return (
                                                        <FormItem>
                                                            <FormLabel className="text-foreground">
                                                                <div className="flex items-center gap-2 flex-wrap">
                                                                    <span>Email*</span>
                                                                    {currentStep !== 1 && (
                                                                        !isEmailEditing ? (
                                                                            <Button
                                                                                type="button"
                                                                                size="sm"
                                                                                variant="outline"
                                                                                className="h-6 px-2 text-xs"
                                                                                onClick={() => {
                                                                                    const currentEmail = String(getValues("email") ?? "")
                                                                                    setEmailValueBeforeEdit(currentEmail)
                                                                                    setIsEmailEditing(true)
                                                                                    if (isNoEmailPlaceholderValue(currentEmail)) {
                                                                                        setValue("email", "", {
                                                                                            shouldDirty: false,
                                                                                            shouldValidate: true,
                                                                                        })
                                                                                    }
                                                                                }}
                                                                            >
                                                                                Edit email
                                                                            </Button>
                                                                        ) : (
                                                                            <Button
                                                                                type="button"
                                                                                size="sm"
                                                                                variant="ghost"
                                                                                className="h-6 px-2 text-xs"
                                                                                onClick={() => {
                                                                                    setValue("email", emailValueBeforeEdit, {
                                                                                        shouldDirty: false,
                                                                                        shouldValidate: true,
                                                                                    })
                                                                                    setIsEmailEditing(false)
                                                                                }}
                                                                            >
                                                                                Cancel email edit
                                                                            </Button>
                                                                        )
                                                                    )}
                                                                    <EmailUpdatesCheckbox control={control} />
                                                                </div>
                                                            </FormLabel>
                                                            <FormControl>
                                                                {!isEmailEditing && currentStep !== 1 ? (
                                                                    <Input
                                                                        type="text"
                                                                        value={getDisplayEmailValue(emailValue)}
                                                                        readOnly
                                                                        disabled
                                                                        className={cn(
                                                                            "h-14 min-h-14 bg-muted transition-colors duration-200",
                                                                            isRequiredEmailMissing && "!border-[var(--color-pizza-600)] focus:!border-[var(--color-pizza-600)] focus:!ring-[var(--color-pizza-600)]/20"
                                                                        )}
                                                                        style={requiredEmailHighlightStyle}
                                                                    />
                                                                ) : (
                                                                    <Input
                                                                        type="email"
                                                                        value={field.value ?? ""}
                                                                        onChange={(e) => field.onChange(e.target.value)}
                                                                        onBlur={field.onBlur}
                                                                        placeholder="Email*"
                                                                        className={cn(
                                                                            "h-14 min-h-14 transition-colors duration-200",
                                                                            isRequiredEmailMissing && "!border-[var(--color-pizza-600)] focus:!border-[var(--color-pizza-600)] focus:!ring-[var(--color-pizza-600)]/20"
                                                                        )}
                                                                        style={requiredEmailHighlightStyle}
                                                                    />
                                                                )}
                                                            </FormControl>
                                                            <FormMessage>{fieldState.error?.message}</FormMessage>
                                                        </FormItem>
                                                    )
                                                }}
                                            />
                                        </div>
                                        {/* Row 2: Address, Organization — titles aligned at top */}
                                        <div className="grid grid-cols-1 gap-5 items-start md:grid-cols-2 md:gap-3">
                                            <StateCountryAutocomplete
                                                setLocationDetails={setLocationDetails}
                                                locationDetails={locationDetails}
                                                control={control}
                                                id="1"
                                                highlightWhenEmpty={true}
                                                label="Address* (including city, state & zip)"
                                            />
                                            <FormOrganizationInput
                                                inputGroupProps={{
                                                    label: "Organization/Church Name or Individual *",
                                                    highlightWhenEmpty: true,
                                                }}
                                                placeholder="Search or type organization name..."
                                                name="organizationName"
                                                control={control}
                                            />
                                        </div>
                                        {/* Row 3: DOB, Age, Gender — aligned */}
                                        <div className="grid grid-cols-1 gap-5 items-end md:grid-cols-3 md:gap-3">
                                            <FormDateInput
                                                inputGroupProps={{ label: "Date of Birth*", highlightWhenEmpty: true }}
                                                placeholder="Select your date of birth"
                                                name="dateOfBirth"
                                                control={control}
                                                enableYearMonthDropdowns={true}
                                                fromYear={new Date().getFullYear() - 100}
                                                toYear={new Date().getFullYear()}
                                                className="h-14 min-h-14"
                                            />
                                            <FormItem className="space-y-0.5 w-full flex flex-col">
                                                <FormLabel className="text-foreground">Age</FormLabel>
                                                <FormControl>
                                                    <Input
                                                        readOnly
                                                        disabled
                                                        value={(() => {
                                                            const age = getAgeFromDateOfBirth(watch("dateOfBirth"))
                                                            return age != null ? `${age} years` : ""
                                                        })()}
                                                        placeholder="—"
                                                        className="h-14 min-h-14 bg-muted"
                                                    />
                                                </FormControl>
                                            </FormItem>
                                            <FormSelectInput
                                                inputGroupProps={{ label: "Gender*", highlightWhenEmpty: true }}
                                                placeholder="Select for lodging logistics"
                                                options={genderOptions}
                                                name="gender"
                                                control={control}
                                            />
                                        </div>
                                    </div>
                                </AccordionContent>
                            </AccordionItem>
                            <AccordionItem value="additional" className="border rounded-lg bg-muted/30 px-4">
                                <AccordionTrigger className="pt-3 pb-0 text-base font-semibold">
                                    Health, Safety & Travel
                                </AccordionTrigger>
                                <AccordionContent className="pt-0">
                                    <div className="space-y-5 md:space-y-4">
                                        <div className="grid grid-cols-1 gap-5 items-end md:grid-cols-2 md:gap-3">
                                            <FormTextareaInput
                                                inputGroupProps={{ label: "Dietary restrictions" }}
                                                placeholder="Any dietary restrictions or allergies"
                                                name="dietaryRestrictions"
                                                control={control}
                                                rows={3}
                                                className={scrollingTextareaClassName}
                                            />
                                            <FormTextareaInput
                                                inputGroupProps={{ label: "Health conditions" }}
                                                placeholder="Any health conditions we should know about"
                                                name="healthConditions"
                                                control={control}
                                                rows={3}
                                                className={scrollingTextareaClassName}
                                            />
                                        </div>
                                        {/* Row 2: Emergency contact 1 – 3 boxes aligned */}
                                        <div className="grid grid-cols-1 gap-5 items-end md:grid-cols-3 md:gap-3">
                                            <FormTextInput
                                                inputGroupProps={{ label: "Emergency contact 1 – Full name" }}
                                                placeholder="Full name"
                                                name="emergencyContact1FullName"
                                                control={control}
                                            />
                                            <FormTextInput
                                                inputGroupProps={{ label: "Emergency contact 1 – Relationship" }}
                                                placeholder="e.g. Spouse, Parent"
                                                name="emergencyContact1Relationship"
                                                control={control}
                                            />
                                            <FormPhoneInput
                                                inputGroupProps={{ label: "Emergency contact 1 – Phone" }}
                                                placeholder="Phone"
                                                name="emergencyContact1Phone"
                                                control={control}
                                            />
                                        </div>
                                        {/* Row 3: Emergency contact 2 – 3 boxes aligned */}
                                        <div className="grid grid-cols-1 gap-5 items-end md:grid-cols-3 md:gap-3">
                                            <FormTextInput
                                                inputGroupProps={{ label: "Emergency contact 2 – Full name" }}
                                                placeholder="Full name"
                                                name="emergencyContact2FullName"
                                                control={control}
                                            />
                                            <FormTextInput
                                                inputGroupProps={{ label: "Emergency contact 2 – Relationship" }}
                                                placeholder="e.g. Spouse, Parent"
                                                name="emergencyContact2Relationship"
                                                control={control}
                                            />
                                            <FormPhoneInput
                                                inputGroupProps={{ label: "Emergency contact 2 – Phone" }}
                                                placeholder="Phone"
                                                name="emergencyContact2Phone"
                                                control={control}
                                            />
                                        </div>
                                        <div className="grid grid-cols-1 gap-5 items-end md:grid-cols-3 md:gap-3">
                                            <FormTextInput
                                                inputGroupProps={{ label: "Health insurance – Provider" }}
                                                placeholder="Provider name"
                                                name="healthInsuranceProvider"
                                                control={control}
                                            />
                                            <FormPhoneInput
                                                inputGroupProps={{ label: "Health insurance – Phone" }}
                                                placeholder="Phone"
                                                name="healthInsurancePhone"
                                                control={control}
                                            />
                                            <FormTextInput
                                                inputGroupProps={{ label: "Health insurance – Policy number" }}
                                                placeholder="Policy number"
                                                name="healthInsurancePolicyNumber"
                                                control={control}
                                            />
                                        </div>
                                        <div className="grid grid-cols-1 gap-5 items-end md:grid-cols-2 md:gap-3">
                                            <FormDateInput
                                                inputGroupProps={{ label: "Background check" }}
                                                placeholder="Select date"
                                                name="backgroundCheck"
                                                control={control}
                                                enableYearMonthDropdowns={true}
                                                fromYear={new Date().getFullYear() - 30}
                                                toYear={new Date().getFullYear() + 1}
                                                className="h-14 min-h-14"
                                            />
                                            {showJamaicaVolunteerTravel && (
                                                <FormTextInput
                                                    inputGroupProps={{ label: "Passport number" }}
                                                    placeholder="Passport number"
                                                    name="passportNumber"
                                                    control={control}
                                                />
                                            )}
                                        </div>
                                        <TravelInsuranceReminderCopy />
                                        <div className="grid grid-cols-1 gap-5 items-end md:grid-cols-3 md:gap-3">
                                            <FormTextInput
                                                inputGroupProps={{ label: "Travel insurance – Provider" }}
                                                placeholder="Provider name"
                                                name="travelInsuranceProvider"
                                                control={control}
                                            />
                                            <FormPhoneInput
                                                inputGroupProps={{ label: "Travel insurance – Phone" }}
                                                placeholder="Phone"
                                                name="travelInsurancePhone"
                                                control={control}
                                            />
                                            <FormTextInput
                                                inputGroupProps={{ label: "Travel insurance – Policy number" }}
                                                placeholder="Policy number"
                                                name="travelInsurancePolicyNumber"
                                                control={control}
                                            />
                                        </div>
                                        <VolunteerTravelSupplement
                                            control={control}
                                            showJamaicaSections={showJamaicaVolunteerTravel}
                                        />
                                    </div>
                                </AccordionContent>
                            </AccordionItem>
                        </Accordion>
                    </DashboardAccordion>

                    <DashboardAccordion heading={PROFILE_ACCORDION_SERVE_DATES} className="bg-white">
                        <div className="space-y-4">
                            <div className="space-y-4">
                                {sortedServeFieldIndices.map((index, displayPos) => {
                                    const field = fields[index]
                                    const displayIdx = displayPos + 1
                                    const isCurrentPeriod = relevantServeFieldIndex === index
                                    return (
                                        <div
                                            key={field.id}
                                            className={cn(
                                                "flex items-start gap-3 rounded-lg border p-3 -m-1 transition-colors",
                                                isCurrentPeriod
                                                    ? "border-emerald-300/90 bg-emerald-50/90 dark:border-emerald-800/60 dark:bg-emerald-950/35"
                                                    : "border-transparent"
                                            )}
                                        >
                                            <div className="flex-1 grid grid-cols-1 gap-3 sm:grid-cols-[2fr,1fr]">
                                                <FormDateRangeInput
                                                    control={control}
                                                    inputGroupProps={{
                                                        label:
                                                            displayIdx === 1
                                                                ? "What are your serve arrival and departure dates?"
                                                                : `Serve Period ${displayIdx}`,
                                                    }}
                                                    name={`servePeriods.${index}`}
                                                />
                                                <div className="min-w-0">
                                                    <FormSelectInput
                                                        name={`servePeriods.${index}.regionId`}
                                                        inputGroupProps={{ label: "Region" }}
                                                        placeholder={regionsLoading ? "Loading regions..." : "Select region..."}
                                                        options={activeRegionOptions}
                                                        control={control}
                                                        loading={regionsLoading}
                                                    />
                                                </div>
                                            </div>
                                            {fields.length > 1 && (
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => fields.length > 1 && remove(index)}
                                                    className="mt-8 text-destructive hover:text-destructive"
                                                >
                                                    <X className="h-4 w-4" />
                                                </Button>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => append({ from: undefined, to: undefined, regionId: undefined })}
                                className="w-full sm:w-auto"
                            >
                                <Plus className="h-4 w-4 mr-2" />
                                Add another serve period
                            </Button>
                        </div>
                    </DashboardAccordion>

                    <AccordionItem
                        value={PROFILE_ACCORDION_EXPERIENCE_SKILLS}
                        className="bg-muted rounded p-4 border flex flex-col gap-3 transition-colors duration-200"
                    >
                        <AccordionTrigger className="py-0 p-1" downChevronIconClassName="w-5 h-5">
                            <div className="text-left">
                                <h2 className="text-xl font-semibold">{PROFILE_ACCORDION_EXPERIENCE_SKILLS}</h2>
                                <p className="text-sm font-normal text-muted-foreground mt-0.5">
                                    (1 = Not Experienced/Interested, 5 = Very Experienced/Interested)
                                </p>
                                {experienceSkillsSummary && (
                                    <p className="text-sm font-normal text-muted-foreground mt-1">
                                        {experienceSkillsSummary}
                                    </p>
                                )}
                            </div>
                        </AccordionTrigger>
                        <AccordionContent className="pb-0 p-1">
                            <div className="grid grid-cols-1 gap-5 items-start md:grid-cols-2 md:gap-4 md:pb-2 xl:grid-cols-2 xl:gap-x-8 xl:gap-y-6">
                                <FormNumberRangeInput
                                    inputGroupProps={{
                                        label: "How experienced are you with general construction?",
                                        wrapperClassName: numberRangeInputWrapperClassName,
                                    }}
                                    name="experienceConstruction"
                                    control={control}
                                />
                                <FormNumberRangeInput
                                    inputGroupProps={{
                                        label: "How interested are you in manual labor?",
                                        wrapperClassName: numberRangeInputWrapperClassName,
                                    }}
                                    name="experienceManualLabor"
                                    control={control}
                                />
                                <FormNumberRangeInput
                                    inputGroupProps={{
                                        label: "How interested are you in leading at a work site?",
                                        wrapperClassName: numberRangeInputWrapperClassName,
                                    }}
                                    name="experienceLeadingTeams"
                                    control={control}
                                />
                                <FormNumberRangeInput
                                    inputGroupProps={{
                                        label: "How experienced are you with drywall finishing?",
                                        wrapperClassName: numberRangeInputWrapperClassName,
                                    }}
                                    name="experienceDrywallFinishing"
                                    control={control}
                                />
                                <FormNumberRangeInput
                                    inputGroupProps={{
                                        label: "How experienced are you with rough carpentry such as framing and laying subfloor?",
                                        wrapperClassName: numberRangeInputWrapperClassName,
                                    }}
                                    name="experienceRoughCarpentry"
                                    control={control}
                                />
                                <FormNumberRangeInput
                                    inputGroupProps={{
                                        label: "How experienced are you with finish carpentry such as installing trim, doors, or cabinets?",
                                        wrapperClassName: numberRangeInputWrapperClassName,
                                    }}
                                    name="experienceFinishCarpentry"
                                    control={control}
                                />
                                <FormNumberRangeInput
                                    inputGroupProps={{
                                        label: "How experienced are you with flooring such as laminate vinyl plank flooring?",
                                        wrapperClassName: numberRangeInputWrapperClassName,
                                    }}
                                    name="experienceFlooring"
                                    control={control}
                                />
                                <FormNumberRangeInput
                                    inputGroupProps={{
                                        label: "How experienced are you with woodworking?",
                                        wrapperClassName: numberRangeInputWrapperClassName,
                                    }}
                                    name="experienceWoodWorking"
                                    control={control}
                                />
                                <FormNumberRangeInput
                                    inputGroupProps={{
                                        label: "How experienced are you with electrical work with 5 being a Journeyman and up?",
                                        wrapperClassName: numberRangeInputWrapperClassName,
                                    }}
                                    name="experienceElectrical"
                                    control={control}
                                />
                                <FormNumberRangeInput
                                    inputGroupProps={{
                                        label: "How experienced are you with plumbing with 5 being a Journeyman and up?",
                                        wrapperClassName: numberRangeInputWrapperClassName,
                                    }}
                                    name="experiencePlumbing"
                                    control={control}
                                />
                                <FormNumberRangeInput
                                    inputGroupProps={{
                                        label: "How experienced are you with HVAC with 5 being a Journeyman and up?",
                                        wrapperClassName: numberRangeInputWrapperClassName,
                                    }}
                                    name="experienceHvac"
                                    control={control}
                                />
                                <FormNumberRangeInput
                                    inputGroupProps={{
                                        label: "How experienced are you with arborist/tree work with 5 being certified?",
                                        wrapperClassName: numberRangeInputWrapperClassName,
                                    }}
                                    name="experienceArborist"
                                    control={control}
                                />
                                <FormNumberRangeInput
                                    inputGroupProps={{
                                        label: "How experienced are you with roofing with 5 being certified?",
                                        wrapperClassName: numberRangeInputWrapperClassName,
                                    }}
                                    name="experienceRoofing"
                                    control={control}
                                />
                                <FormNumberRangeInput
                                    inputGroupProps={{
                                        label: "How experienced are you with heavy equipment with 5 being certified?",
                                        wrapperClassName: numberRangeInputWrapperClassName,
                                    }}
                                    name="experienceHeavyEquipment"
                                    control={control}
                                />
                                <FormNumberRangeInput
                                    inputGroupProps={{
                                        label: "How experienced are you with google programs/data management?",
                                        wrapperClassName: numberRangeInputWrapperClassName,
                                    }}
                                    name="experienceDataManagement"
                                    control={control}
                                />
                                <FormNumberRangeInput
                                    inputGroupProps={{
                                        label: "How experienced are you with storytelling/social media?",
                                        wrapperClassName: numberRangeInputWrapperClassName,
                                    }}
                                    name="experienceStorytelling"
                                    control={control}
                                />
                                <FormNumberRangeInput
                                    inputGroupProps={{
                                        label: "How interested are you in Alternative Ministry opportunities, such as visiting the nursing home, kid's mentorship, or food/supply distribution events?",
                                        wrapperClassName: numberRangeInputWrapperClassName,
                                    }}
                                    name="experienceAltMinistries"
                                    control={control}
                                />
                                <FormNumberRangeInput
                                    inputGroupProps={{
                                        label: "How interested would you be in cooking meals for all of the groups/volunteers during the week?",
                                        wrapperClassName: numberRangeInputWrapperClassName,
                                    }}
                                    name="mealPreparationAbility"
                                    control={control}
                                />
                                <div className="col-span-full grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-3 md:items-end">
                                    <FormSelectInput
                                        inputGroupProps={{
                                            label: "Do you speak any other language besides English?",
                                        }}
                                        placeholder="Select One"
                                        options={otherLanguageOptions}
                                        name="otherLanguages"
                                        control={control}
                                    />
                                </div>
                                <div className="col-span-full grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-3 md:items-end">
                                    <FormSelectInput
                                        inputGroupProps={{ label: "T-shirt size" }}
                                        placeholder="Select for potential addon"
                                        options={tShirtSizeOptions}
                                        name="tShirtSize"
                                        control={control}
                                    />
                                    <FormYesNoInput
                                        inputGroupProps={{
                                            label: "Have you served before in disaster relief?",
                                        }}
                                        name="hasServedBefore"
                                        control={control}
                                    />
                                </div>
                                <div className="col-span-full space-y-2">
                                    <div className="flex flex-wrap items-center gap-2 w-full min-w-0">
                                        <FormLabel className="text-sm font-medium min-w-0 flex-1 basis-0 break-words">What else would you like us know concerning your skills, experience or expectations?</FormLabel>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setWhatElseExpanded(!whatElseExpanded)}
                                            className="h-6 w-6 p-0 shrink-0 text-muted-foreground hover:text-foreground"
                                            aria-label={whatElseExpanded ? "Collapse" : "Expand to read more"}
                                        >
                                            <ChevronDown className={cn("h-4 w-4 transition-transform duration-200", whatElseExpanded && "rotate-180")} />
                                        </Button>
                                    </div>
                                    <FormItem>
                                        <FormControl>
                                            <div className={cn("overflow-y-auto rounded-md border border-input bg-background transition-[max-height] duration-200", whatElseExpanded ? "max-h-[320px]" : "max-h-[72px]")}>
                                                <Textarea
                                                    {...skillsAndQualitiesField.field}
                                                    value={skillsAndQualitiesField.field.value ?? ""}
                                                    onChange={(e) => skillsAndQualitiesField.field.onChange(e.target.value)}
                                                    placeholder="Mention skills or qualities"
                                                    rows={3}
                                                    className="min-h-0 border-0 resize-none focus-visible:ring-0 focus-visible:ring-offset-0 rounded-md"
                                                />
                                            </div>
                                        </FormControl>
                                        <FormMessage>{skillsAndQualitiesField.fieldState.error?.message}</FormMessage>
                                    </FormItem>
                                </div>
                            </div>
                        </AccordionContent>
                    </AccordionItem>

                    <AccordionItem
                        value={PROFILE_ACCORDION_VEHICLE}
                        className="bg-white rounded p-4 border flex flex-col gap-3 transition-colors duration-200"
                    >
                        <AccordionTrigger className="py-0 p-1" downChevronIconClassName="w-5 h-5">
                            <div className="text-left">
                                <h2 className="text-xl font-semibold">{PROFILE_ACCORDION_VEHICLE}</h2>
                                <p className="text-sm font-normal text-muted-foreground mt-0.5">
                                    Tell us about the vehicle you will have on site
                                </p>
                            </div>
                        </AccordionTrigger>
                        <AccordionContent className="pb-0 p-1">
                            <div className="grid grid-cols-1 gap-5 items-start md:grid-cols-2 md:gap-4 md:pb-2 xl:grid-cols-2 xl:gap-x-8 xl:gap-y-6">
                                <FormTextInput
                                    inputGroupProps={{ label: "Make" }}
                                    placeholder="e.g. Toyota, Ford"
                                    name="vehicleMake"
                                    control={control}
                                />
                                <FormTextInput
                                    inputGroupProps={{ label: "Model" }}
                                    placeholder="e.g. Camry, F-150"
                                    name="vehicleModel"
                                    control={control}
                                />
                                <FormTextInput
                                    inputGroupProps={{ label: "Color" }}
                                    placeholder="e.g. White, Silver"
                                    name="vehicleColor"
                                    control={control}
                                />
                                <FormTextInput
                                    inputGroupProps={{ label: "License plate number" }}
                                    placeholder="License plate number"
                                    name="vehicleLicensePlateNumber"
                                    control={control}
                                />
                                <FormTextInput
                                    inputGroupProps={{ label: "License plate state" }}
                                    placeholder="e.g. TN, NC"
                                    name="vehicleLicensePlateState"
                                    control={control}
                                />
                            </div>
                        </AccordionContent>
                    </AccordionItem>

                    <DashboardAccordion heading={PROFILE_ACCORDION_PRAYER_REQUESTS} className="bg-muted">
                        <div className="space-y-2">
                            <div className="flex flex-nowrap items-center gap-2 w-full min-w-0">
                                <FormLabel className="text-sm font-medium shrink-0">Prayer Requests</FormLabel>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setPrayerRequestsExpanded(!prayerRequestsExpanded)}
                                    className="h-6 w-6 p-0 shrink-0 ml-auto text-muted-foreground hover:text-foreground"
                                    aria-label={prayerRequestsExpanded ? "Collapse" : "Expand to read more"}
                                >
                                    <ChevronDown className={cn("h-4 w-4 transition-transform duration-200", prayerRequestsExpanded && "rotate-180")} />
                                </Button>
                            </div>
                            <FormItem>
                                <FormControl>
                                    <div className={cn("overflow-y-auto rounded-md border border-input bg-background transition-[max-height] duration-200", prayerRequestsExpanded ? "max-h-[320px]" : "max-h-[72px]")}>
                                        <Textarea
                                            {...prayerRequestField.field}
                                            value={prayerRequestField.field.value ?? ""}
                                            onChange={(e) => prayerRequestField.field.onChange(e.target.value)}
                                            placeholder="Enter your prayer requests"
                                            rows={3}
                                            className="min-h-0 border-0 resize-none focus-visible:ring-0 focus-visible:ring-offset-0 rounded-md"
                                        />
                                    </div>
                                </FormControl>
                                <FormMessage>{prayerRequestField.fieldState.error?.message}</FormMessage>
                            </FormItem>
                        </div>
                    </DashboardAccordion>
                </Accordion>
            </form>
            <StickySaveBar 
                isVisible={
                    (isFormInitialized || currentStep === 1) &&
                    ((isDirty && Object.keys(dirtyFields).length > 0) || showSuccessBar)
                }
                isSuccess={showSuccessBar}
                isSubmitting={isSubmitting}
                onSave={handleStickySave}
            />
        </FormProvider>
    )
}

export default ProfileAboutMeForm
